/**
 * The orientation anchor above a card collection.
 *
 * In the Cards layout there is no single widget to hold the set's identity, so
 * the set gets one bar of its own above the cards it describes — and, when the
 * projection has a bound to state, a second line of its own beneath that.
 *
 * Its truncation order is deliberate and it is the opposite of what a flex row
 * does by default: the identity region is the LAST thing to give, the context
 * region wraps and shrinks first, and the count stays bound to the label
 * instead of being pushed to the far edge where it reads as a page number.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import {
  CRC_ACCENT,
  CRC_NUMERIC_STYLE,
  CRC_R_PILL,
  CRC_TEXT_FAINT,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import { CresultcardTextStep } from "./cresultcard.types";

const BAR_GLYPH_SIZE = 13;
const BAR_GLYPH_STROKE = 1.25;
const BOUNDS_FONT_SIZE = 10;

interface CresultcardSetBarProps {
  readonly icon: TablerIcon;
  readonly label: string;
  readonly context?: ReactNode;
  /** What bounds the projection. Wraps to its own line rather than clipping. */
  readonly bounds?: ReactNode;
  /**
   * How many results the set delivered. ABSENT while the set wears a face: a
   * count is a statement about a delivered answer, and a bar reading `3` beside
   * `No listings here` is the set contradicting itself in one line.
   */
  readonly count?: number;
}

export function CresultcardSetBar({
  icon: Glyph,
  label,
  context,
  bounds,
  count,
}: CresultcardSetBarProps): ReactElement {
  return (
    <div
      data-el="Cresultcard/SetBar/Root"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 10,
        minWidth: 0,
        paddingBlock: 9,
        paddingInline: 14,
        borderRadius: CRC_R_PILL,
        backgroundColor: "rgba(0,0,0,0.28)",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          // Last to give: the reader has to know WHAT they are looking at
          // before they need to know how much of it there is.
          flex: "0 0 auto",
          minWidth: 0,
        }}
      >
        <Glyph
          color={CRC_ACCENT}
          size={BAR_GLYPH_SIZE}
          stroke={BAR_GLYPH_STROKE}
        />
        <span
          style={CRC_TYPE_STYLE[CresultcardTextStep.Eyebrow]}
        >
          {label}
        </span>
        {count != null && (
          <span
            style={{
              ...CRC_NUMERIC_STYLE,
              fontSize: 10,
              fontWeight: 700,
              color: CRC_TEXT_FAINT,
            }}
          >
            {count}
          </span>
        )}
      </span>
      {context != null && (
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            ...CRC_NUMERIC_STYLE,
            flex: "0 1 auto",
            minWidth: 0,
          }}
        >
          {context}
        </span>
      )}
      {bounds != null && (
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            ...CRC_NUMERIC_STYLE,
            // Takes the whole of its own line: this bar already wraps, and a
            // bound sharing a row with the identity is the first thing to be
            // clipped. `3 of 18 · no sort applied` cut in half states the
            // opposite of what it was written to state.
            flexBasis: "100%",
            minWidth: 0,
            fontSize: BOUNDS_FONT_SIZE,
            color: CRC_TEXT_FAINT,
          }}
        >
          {bounds}
        </span>
      )}
    </div>
  );
}
